/*
 * The words of the bank's search box that mean more than text
 * (utils/bankItemFilter): a date, a month, a year or a file type. Each one
 * is still matched as text too — a term only ever widens what its word
 * finds, so a file named "kuvat 9.10." is found by "9.10." as before.
 *
 * Numbers are read the way the cards show a date, DD.MM.YYYY, in every
 * language; ISO dates (2026-10-09) work as well. Month names and the type
 * words come from the user's language.
 */

/** A month word is read from this many letters on: "lok", "oct". */
const MIN_MONTH_PREFIX = 3;
/** A word that starts with a whole month name is that month: "lokakuussa". */
const MIN_INFLECTED_MONTH = 5;

/** The type facet's words: its plural chip label and a singular one. */
const TYPE_LABEL_KEYS = {
  folder: ['bank.type_folder', 'bank.type_folders'],
  image: ['bank.type_image', 'bank.type_images'],
  video: ['bank.type_video', 'bank.type_videos'],
  pdf: ['bank.type_pdf'],
  document: ['bank.type_document', 'bank.type_documents'],
  spreadsheet: ['bank.type_spreadsheet', 'bank.type_spreadsheets'],
  other: ['bank.type_other_singular', 'bank.type_other'],
};

const DOTTED_DATE = /^(\d{1,2})\.(\d{1,2})(?:\.(\d{0,4}))?$/;
const ISO_DATE = /^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/;
const YEAR = /^(19|20)\d{2}$/;
const DAY = /^(\d{1,2})\.?$/;

const monthNamesByLang = {};

const toMonthNames = (lang) => {
  const long = new Intl.DateTimeFormat(lang, { month: 'long' });
  const short = new Intl.DateTimeFormat(lang, { month: 'short' });
  // The month inside a date, where a language inflects it: "lokakuuta",
  // "октября".
  const inDate = new Intl.DateTimeFormat(lang, {
    day: 'numeric',
    month: 'long',
  });
  return Array.from({ length: 12 }, (_, month) => {
    const date = new Date(2000, month, 15);
    const inDateMonth = inDate
      .formatToParts(date)
      .find((part) => part.type === 'month');
    const names = [
      long.format(date),
      short.format(date),
      inDateMonth ? inDateMonth.value : '',
    ]
      .map((name) => name.toLowerCase().replace(/\.$/, ''))
      .filter(Boolean);
    return Array.from(new Set(names));
  });
};

/**
 * The names of the twelve months in a language, lowercased: the standalone
 * and the in-date form and the abbreviation ("lokakuu", "lokakuuta",
 * "loka"). One cached list per language.
 * @param {string} lang i18next language code
 * @returns {Array<string[]>} index 0 is January
 */
export function getBankSearchMonthNames(lang) {
  const key = lang || 'en';
  if (!monthNamesByLang[key]) {
    try {
      monthNamesByLang[key] = toMonthNames(key);
    } catch (error) {
      monthNamesByLang[key] = toMonthNames('en');
    }
  }
  return monthNamesByLang[key];
}

/**
 * The type facet's words in the user's language, lowercased, to the format
 * key each stands for ("kuva" and "kuvat" → 'image'). A label missing from
 * the translations is no word.
 * @param {Function} t i18next translation function (namespace 'common')
 * @returns {Object<string, string>}
 */
export function getBankSearchTypeWords(t) {
  const words = {};
  Object.keys(TYPE_LABEL_KEYS).forEach((formatKey) => {
    TYPE_LABEL_KEYS[formatKey].forEach((labelKey) => {
      const label = typeof t === 'function' ? t(labelKey) : '';
      if (typeof label !== 'string' || !label || label === labelKey) return;
      words[label.toLowerCase()] = formatKey;
    });
  });
  return words;
}

const stripPunctuation = (token) => token.replace(/,+$/, '');

const monthsOfWord = (word, monthNames) => {
  const letters = word.replace(/\.$/, '');
  if (letters.length < MIN_MONTH_PREFIX || /\d/.test(letters)) return null;
  const months = [];
  monthNames.forEach((names, month) => {
    const found = names.some(
      (name) =>
        name.startsWith(letters) ||
        (name.length >= MIN_INFLECTED_MONTH && letters.startsWith(name))
    );
    if (found) months.push(month);
  });
  return months.length ? months : null;
};

const isDay = (value) => value >= 1 && value <= 31;
const isMonth = (value) => value >= 1 && value <= 12;

// A year as far as it has been typed: "2" and "20" are on the way to 2026,
// and two digits are also the short form of the year ("26").
const matchesYearPrefix = (year, typed) =>
  !typed ||
  String(year).startsWith(typed) ||
  (typed.length === 2 && year % 100 === Number(typed));

const toDateTest = (word, isNextToMonth, monthNames) => {
  const dotted = DOTTED_DATE.exec(word);
  if (dotted) {
    const day = Number(dotted[1]);
    const month = Number(dotted[2]);
    if (!isDay(day) || !isMonth(month)) return null;
    const typedYear = dotted[3] || '';
    return (date) =>
      date.day === day &&
      date.month === month - 1 &&
      matchesYearPrefix(date.year, typedYear);
  }
  const iso = ISO_DATE.exec(word);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = iso[3] ? Number(iso[3]) : null;
    if (!isMonth(month) || (day !== null && !isDay(day))) return null;
    return (date) =>
      date.year === year &&
      date.month === month - 1 &&
      (day === null || date.day === day);
  }
  if (YEAR.test(word)) {
    const year = Number(word);
    return (date) => date.year === year;
  }
  // A bare number is a day only beside a month, "9. lokakuuta" or
  // "October 9": on its own it is far likelier part of a file name.
  const day = DAY.exec(word);
  if (day && isNextToMonth && isDay(Number(day[1]))) {
    const value = Number(day[1]);
    return (date) => date.day === value;
  }
  const months = monthsOfWord(word, monthNames);
  if (months) return (date) => months.includes(date.month);
  return null;
};

/**
 * Reads the search box's words (bankItemFilter searchTokensOf) for what
 * else each can mean. Every term keeps its `text`; `testDate` is set for a
 * date, a month, a year or a day beside a month, and `formatKey` for a type
 * word.
 * @param {string[]} tokens lowercased words
 * @param {?{monthNames: Array<string[]>, typeWords: Object<string, string>}} vocabulary
 *   getBankSearchMonthNames and getBankSearchTypeWords of the user's
 *   language; without it every term is text alone
 * @returns {Array<{text: string, testDate: ?Function, formatKey: ?string}>}
 *   `testDate` takes `{year, month, day}` in local time, month 0-based
 */
export function compileBankSearchTerms(tokens, vocabulary) {
  if (!vocabulary) {
    return tokens.map((text) => ({ text, testDate: null, formatKey: null }));
  }
  const monthNames = vocabulary.monthNames || [];
  const typeWords = vocabulary.typeWords || {};
  const words = tokens.map(stripPunctuation);
  const isMonthWord = words.map((word) => !!monthsOfWord(word, monthNames));
  return tokens.map((text, index) => ({
    text,
    testDate: toDateTest(
      words[index],
      !!(isMonthWord[index - 1] || isMonthWord[index + 1]),
      monthNames
    ),
    formatKey: Object.prototype.hasOwnProperty.call(typeWords, words[index])
      ? typeWords[words[index]]
      : null,
  }));
}
